import { IRecordItem } from '@amc-technology/davinci-api';
import { IScreenPopToUrlConfig } from '../Model/Interfaces/IScreenPopToUrlConfig';

interface ICadFieldValue {
  Value: any;
}

interface IScenarioCadMap {
  [fieldName: string]: ICadFieldValue;
}

interface IScenarioCadStore {
  [scenarioId: string]: IScenarioCadMap;
}

export function parseScreenPopToUrlConfig(screenPopConfig: any): IScreenPopToUrlConfig | null {
  try {
    const spVars = screenPopConfig?.variables;
    if (!spVars || !spVars['ScreenpopRelatedRecords']) {
      return null;
    }

    const selectedType = spVars['ScreenpopRelatedRecords'];
    if (typeof selectedType !== 'string') {
      return null;
    }

    let url = '';

    if (selectedType === 'ScreenpopToURL') {
      const selectOptions = spVars['ScreenpopRelatedRecords_options'] || spVars['ScreenpopRelatedRecords'];
      if (typeof selectOptions === 'object' && selectOptions['ScreenpopToURL']) {
        url = selectOptions['ScreenpopToURL'] as string;
      } else if (typeof spVars['ScreenpopToURL'] === 'string') {
        url = spVars['ScreenpopToURL'] as string;
      }
    } else if (selectedType.startsWith('http://') || selectedType.startsWith('https://') || selectedType.startsWith('/')) {
      url = selectedType;
    }

    if (!url) {
      return null;
    }

    const rawParameterMapping = spVars['ScreenpopToURLParameterMap'];
    const parameterMapping = rawParameterMapping && typeof rawParameterMapping === 'object' && !Array.isArray(rawParameterMapping) ? rawParameterMapping : {};

    return {
      url,
      parameterMapping
    };
  } catch {
    return null;
  }
}

export function buildScreenPopToUrl(
  config: IScreenPopToUrlConfig | null,
  scenarioToCADMap: IScenarioCadStore,
  record: IRecordItem | null,
  scenarioId: string
): string {
  try {
    if (!config || !config.url) {
      return '';
    }

    const params: string[] = [];
    const cadMap = scenarioToCADMap[scenarioId] || {};

    for (const [paramName, tokenValue] of Object.entries(config.parameterMapping || {})) {
      let resolvedValue = '';

      if (typeof tokenValue === 'string' && tokenValue.startsWith('CAD.')) {
        const cadField = tokenValue.substring(4);
        const cadEntry = cadMap[cadField];
        if (cadEntry && cadEntry.Value !== undefined && cadEntry.Value !== null) {
          resolvedValue = String(cadEntry.Value);
        }
      } else if (typeof tokenValue === 'string' && tokenValue.startsWith('Record.')) {
        if (record) {
          const fieldName = tokenValue.substring(7);
          if (fieldName === 'Id') {
            resolvedValue = record.id;
          } else if (fieldName === 'Type') {
            resolvedValue = record.type;
          } else if (record.fields && record.fields[fieldName] && record.fields[fieldName].Value !== undefined && record.fields[fieldName].Value !== null) {
            resolvedValue = String(record.fields[fieldName].Value);
          }
        }
      } else if (tokenValue === 'timestamp') {
        resolvedValue = new Date().toISOString();
      } else if (tokenValue !== undefined && tokenValue !== null) {
        resolvedValue = String(tokenValue);
      }

      params.push(`${encodeURIComponent(paramName)}=${encodeURIComponent(resolvedValue)}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return `${config.url}${queryString}`;
  } catch {
    return config?.url || '';
  }
}

export function getScreenPopTargetEntityKey(quickCreateEntities: any): string | null {
  try {
    for (const key of Object.keys(quickCreateEntities || {})) {
      const value = quickCreateEntities[key];
      if (typeof value === 'string' && value.toLowerCase().includes('screenpop_target')) {
        return key;
      }
    }

    return null;
  } catch {
    return null;
  }
}

export function getVisibleQuickCreateEntities(
  quickCreateEntities: any,
  screenPopTargetEntityKey: string | null,
  isScreenPopTargetAvailable: boolean
): any {
  try {
    const entities = { ...(quickCreateEntities || {}) };

    if (!isScreenPopTargetAvailable && screenPopTargetEntityKey) {
      delete entities[screenPopTargetEntityKey];
    }

    return entities;
  } catch {
    return {};
  }
}

export function resolveSelectedRecord(recordList: IRecordItem[], selectedId: string | undefined): IRecordItem | null {
  try {
    if (!recordList || recordList.length === 0) {
      return null;
    }

    if (selectedId && selectedId !== 'DefaultMultiMatch') {
      return recordList.find((record: IRecordItem) => record.id === selectedId) || recordList[0];
    }

    return recordList[0];
  } catch {
    return null;
  }
}
